import { motion } from "framer-motion";
import { Mail, Linkedin, ArrowRight, Calendar } from "lucide-react";

const ContactPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-mist">
        <div className="container-custom">
          <div className="max-w-3xl">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Contact
            </motion.span>
            <motion.h1 
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="heading-1 mb-6"
              data-testid="contact-headline"
            >
              Laten we in gesprek gaan
            </motion.h1>
            <motion.p 
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="body-large"
            >
              Klaar om de omzet eindelijk terug te zien als cash op de bank? Plan een 
              afspraak voor een vrijblijvend gesprek of stel je vraag via de mail.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Calendly Booking */}
            <motion.div {...fadeUp}>
              <div className="bg-burgundy text-white p-8 md:p-12 h-full">
                <Calendar className="w-12 h-12 text-cta mb-6" strokeWidth={1} />
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">
                  Plan direct een gesprek
                </h2>
                <p className="text-white/70 mb-8 leading-relaxed">
                  Kies een moment dat je schikt via Calendly. In een gesprek van 
                  30 minuten bespreken we jouw situatie en waar de kansen liggen.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    "Vrijblijvend kennismakingsgesprek",
                    "Eerste analyse van jouw situatie",
                    "Concrete inzichten en aanbevelingen"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-white/90">
                      <div className="w-1.5 h-1.5 bg-cta rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>

                <a
                  href="https://calendly.com/rabia-regieopcashflow/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calendly-button"
                  className="bg-cta text-white px-8 py-4 rounded-sm hover:bg-white hover:text-burgundy transition-all duration-300 text-sm tracking-widest uppercase font-semibold inline-flex items-center gap-2 w-full justify-center md:w-auto"
                >
                  Open Calendly
                  <ArrowRight size={18} />
                </a>
              </div>
            </motion.div>

            {/* Contact Information */}
            <div>
              <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
                <h2 className="heading-3 mb-8">Direct contact</h2>
                
                <div className="space-y-6 mb-12">
                  <a
                    href="mailto:rabia@regieopcashflow.nl"
                    data-testid="contact-email-link"
                    className="flex items-center gap-4 p-6 bg-mist hover:bg-cta/10 transition-colors duration-300 group"
                  >
                    <div className="p-4 bg-burgundy text-white rounded-sm group-hover:bg-cta transition-colors duration-300">
                      <Mail size={24} strokeWidth={1} />
                    </div>
                    <div>
                      <span className="text-sm text-navy/70 block mb-1">Email</span>
                      <span className="text-navy font-medium text-lg">rabia@regieopcashflow.nl</span>
                    </div>
                  </a>

                  <a
                    href="https://www.linkedin.com/in/rabia-boutkabout-857ab685/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin-link"
                    className="flex items-center gap-4 p-6 bg-mist hover:bg-cta/10 transition-colors duration-300 group"
                  >
                    <div className="p-4 bg-burgundy text-white rounded-sm group-hover:bg-cta transition-colors duration-300">
                      <Linkedin size={24} strokeWidth={1} />
                    </div>
                    <div>
                      <span className="text-sm text-navy/70 block mb-1">LinkedIn</span>
                      <span className="text-navy font-medium text-lg">Rabia Boutkabout</span>
                    </div>
                  </a>
                </div>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-mist p-8">
                <h3 className="font-serif text-xl text-navy mb-4">
                  Wat kun je verwachten?
                </h3>
                <div className="space-y-4 text-navy/70">
                  <p>
                    In een eerste gesprek nemen we de tijd om jouw situatie te 
                    begrijpen. Waar loopt de omzet vast? Welke processen zorgen 
                    voor vertraging?
                  </p>
                  <p>
                    Je ontvangt direct eerste inzichten en een eerlijke inschatting 
                    of en hoe Regie op Cashflow kan helpen.
                  </p>
                  <p className="font-medium text-berry">
                    Geen verplichtingen, wel concrete waarde.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="section-padding bg-mist">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2 {...fadeUp} className="heading-2 mb-6">
              Werkgebied
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.1 }} className="body-large">
              Regie op Cashflow werkt met professionele dienstverlenende 
              organisaties door heel Nederland. Afstemming vindt plaats op 
              locatie bij de organisatie of via videobellen, afhankelijk van 
              wat het beste past bij het traject.
            </motion.p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
